/**
 * ManualEntryModal Component
 * 
 * Modal for manual food entry and AI analysis.
 * P0 FIX: Button hierarchy - Analyze is PRIMARY, Close is secondary (X button)
 * ENHANCED: Editable macro inputs with visual confirmation animation
 */

import React, { useState, useEffect } from 'react';
import { NutritionDataDisclaimer } from '../ui/UtilityComponents';
import VoiceInputButton from '../voice/VoiceInputButton';

const ManualEntryModal = ({
  // State
  manualEntryText,
  manualEntryResult,
  analyzing,
  
  // Setters & Handlers
  setManualEntryText,
  setManualEntryResult,
  setShowManualEntry,
  analyzeManualEntry,
  addManualEntryToMeals
}) => {
  // Local state for macro overrides - reset when result changes
  const [macroOverrides, setMacroOverrides] = useState({
    calories: null,
    protein: null,
    carbs: null,
    fat: null
  });
  
  // Track which field was just edited for animation
  const [justEdited, setJustEdited] = useState(null);
  
  // Track previous result to detect changes
  const [prevResultId, setPrevResultId] = useState(null);
  const currentResultId = manualEntryResult?.food_items;
  
  // Reset overrides when result changes (using derived state pattern)
  if (currentResultId !== prevResultId) {
    setPrevResultId(currentResultId);
    if (currentResultId && prevResultId !== null) {
      setMacroOverrides({ calories: null, protein: null, carbs: null, fat: null });
    }
  }
  
  // Clear animation after delay
  useEffect(() => {
    if (justEdited) {
      const timer = setTimeout(() => setJustEdited(null), 600);
      return () => clearTimeout(timer);
    }
  }, [justEdited]);

  const handleClose = () => {
    setShowManualEntry(false);
    setManualEntryText('');
    setManualEntryResult(null);
    setMacroOverrides({ calories: null, protein: null, carbs: null, fat: null });
  };
  
  // Track which field is actively being edited + its local string value
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');

  const handleMacroFocus = (field, currentValue) => {
    setEditingField(field);
    setEditValue(String(currentValue));
    setTimeout(() => {
      document.activeElement?.select();
    }, 0);
    setTimeout(() => {
      document.activeElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  };

  const handleMacroBlur = (field) => {
    const parsed = parseInt(editValue) || 0;
    setMacroOverrides(prev => ({ ...prev, [field]: parsed }));
    setJustEdited(field);
    setEditingField(null);
  };

  const handleMacroChange = (field, value) => {
    const cleaned = value.replace(/[^0-9]/g, '');
    setEditValue(cleaned);
  };
  
  const handleAddToMeals = () => {
    // Create modified result with overrides
    const finalResult = {
      ...manualEntryResult,
      calories: macroOverrides.calories ?? manualEntryResult.calories,
      protein: macroOverrides.protein ?? manualEntryResult.protein,
      carbs: macroOverrides.carbs ?? manualEntryResult.carbs,
      fat: macroOverrides.fat ?? manualEntryResult.fat
    };
    
    // Pass the final result directly to the parent function
    addManualEntryToMeals(finalResult);
  };
  
  const hasOverrides = macroOverrides.calories !== null || 
                       macroOverrides.protein !== null || 
                       macroOverrides.carbs !== null || 
                       macroOverrides.fat !== null;

  // Animation keyframes injected via style
  const pulseAnimation = `
    @keyframes macroPulse {
      0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.4); }
      50% { transform: scale(1.02); box-shadow: 0 0 0 8px rgba(102, 126, 234, 0); }
      100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(102, 126, 234, 0); }
    }
  `;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <style>{pulseAnimation}</style>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
        {/* Close X button - Secondary, top-right */}
        <button 
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            padding: '4px 8px',
            lineHeight: 1
          }}
          aria-label="Close"
        >
          ×
        </button>

        <h2 className="heading-3">✍️ Manual Food Entry</h2>
        <p className="modal-description">Describe your meal in detail — or tap the mic to speak it</p>
        
        {/* Voice Input — transcribes into the textarea below */}
        <VoiceInputButton
          onTranscript={(text) => {
            setManualEntryText((prev) => (prev && prev.trim().length > 0)
              ? `${prev.trim()} ${text}`
              : text);
          }}
          contextHint="meal log with common food names, portions, and preparation methods"
          disabled={analyzing}
        />

        {/* Input Section */}
        <div style={{ marginTop: '16px' }}>
          <textarea
            className="manual-entry-textarea"
            data-testid="manual-entry-textarea"
            placeholder="e.g., Grilled chicken breast with brown rice and steamed broccoli"
            value={manualEntryText}
            onChange={(e) => setManualEntryText(e.target.value)}
            rows="4"
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: '10px',
              border: '1px solid var(--border-color)',
              background: 'var(--input-bg)',
              color: 'var(--text-primary)',
              fontSize: '15px',
              resize: 'vertical',
              marginBottom: '16px'
            }}
          />
          
          {/* PRIMARY CTA: Analyze - Full width, prominent */}
          <button 
            onClick={analyzeManualEntry}
            disabled={analyzing || !manualEntryText.trim()}
            style={{
              width: '100%',
              padding: '16px 24px',
              fontSize: '16px',
              fontWeight: '600',
              background: analyzing || !manualEntryText.trim() 
                ? 'rgba(102, 126, 234, 0.5)' 
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: analyzing || !manualEntryText.trim() ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              minHeight: '54px'
            }}
          >
            {analyzing ? '🔄 Analyzing...' : '🔍 Analyze My Meal'}
          </button>
        </div>

        {/* Analysis Result */}
        {manualEntryResult && (
          <div style={{
            marginTop: '20px',
            padding: '16px',
            background: 'rgba(76, 175, 80, 0.08)',
            borderRadius: '12px',
            border: '1px solid rgba(76, 175, 80, 0.2)'
          }}>
            <h3 style={{ 
              margin: '0 0 12px 0', 
              fontSize: '16px', 
              fontWeight: '600',
              color: 'var(--text-primary)'
            }}>
              {manualEntryResult.food_items}
            </h3>
            
            {/* Instruction hint */}
            <p style={{ 
              fontSize: '11px', 
              color: 'var(--text-muted)', 
              marginBottom: '12px', 
              textAlign: 'center' 
            }}>
              📊 Tap any value to adjust if AI over/underestimated
            </p>
            
            {/* Editable Macro Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '8px',
              marginBottom: '16px'
            }}>
              {/* Calories */}
              <div 
                data-testid="manual-entry-calories"
                style={{ 
                  padding: '10px', 
                  background: 'rgba(255,255,255,0.05)', 
                  borderRadius: '8px',
                  textAlign: 'center',
                  border: macroOverrides.calories !== null ? '2px solid #667eea' : '1px solid transparent',
                  animation: justEdited === 'calories' ? 'macroPulse 0.6s ease-out' : 'none',
                  transition: 'border-color 0.2s ease'
                }}
              >
                <input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  data-testid="manual-macro-input-calories"
                  value={editingField === 'calories' ? editValue : String(macroOverrides.calories ?? manualEntryResult.calories ?? 0)}
                  onFocus={() => handleMacroFocus('calories', macroOverrides.calories ?? manualEntryResult.calories ?? 0)}
                  onChange={(e) => handleMacroChange('calories', e.target.value)}
                  onBlur={() => handleMacroBlur('calories')}
                  style={{
                    width: '70px',
                    padding: '4px',
                    border: 'none',
                    background: 'transparent',
                    color: macroOverrides.calories !== null ? '#667eea' : 'var(--text-primary)',
                    fontSize: '20px',
                    fontWeight: '700',
                    textAlign: 'center',
                    WebkitAppearance: 'none',
                    MozAppearance: 'textfield'
                  }}
                />
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block' }}>
                  kcal {macroOverrides.calories !== null && '✓'}
                </span>
              </div>
              
              {/* Protein */}
              <div 
                data-testid="manual-entry-protein"
                style={{ 
                  padding: '10px', 
                  background: 'rgba(255,255,255,0.05)', 
                  borderRadius: '8px', 
                  textAlign: 'center',
                  border: macroOverrides.protein !== null ? '2px solid #667eea' : '1px solid transparent',
                  animation: justEdited === 'protein' ? 'macroPulse 0.6s ease-out' : 'none',
                  transition: 'border-color 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                  <input
                    inputMode="numeric"
                    pattern="[0-9]*"
                    data-testid="manual-macro-input-protein"
                    value={editingField === 'protein' ? editValue : String(macroOverrides.protein ?? manualEntryResult.protein ?? 0)}
                    onFocus={() => handleMacroFocus('protein', macroOverrides.protein ?? manualEntryResult.protein ?? 0)}
                    onChange={(e) => handleMacroChange('protein', e.target.value)}
                    onBlur={() => handleMacroBlur('protein')}
                    style={{
                      width: '50px',
                      padding: '4px',
                      border: 'none',
                      background: 'transparent',
                      color: macroOverrides.protein !== null ? '#667eea' : '#4CAF50',
                      fontSize: '18px',
                      fontWeight: '600',
                      textAlign: 'center',
                      WebkitAppearance: 'none',
                      MozAppearance: 'textfield'
                    }}
                  />
                  <span style={{ fontSize: '14px', color: macroOverrides.protein !== null ? '#667eea' : '#4CAF50' }}>g</span>
                </div>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block' }}>
                  Protein {macroOverrides.protein !== null && '✓'}
                </span>
              </div>
              
              {/* Carbs */}
              <div 
                data-testid="manual-entry-carbs"
                style={{ 
                  padding: '10px', 
                  background: 'rgba(255,255,255,0.05)', 
                  borderRadius: '8px', 
                  textAlign: 'center',
                  border: macroOverrides.carbs !== null ? '2px solid #667eea' : '1px solid transparent',
                  animation: justEdited === 'carbs' ? 'macroPulse 0.6s ease-out' : 'none',
                  transition: 'border-color 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                  <input
                    inputMode="numeric"
                    pattern="[0-9]*"
                    data-testid="manual-macro-input-carbs"
                    value={editingField === 'carbs' ? editValue : String(macroOverrides.carbs ?? manualEntryResult.carbs ?? 0)}
                    onFocus={() => handleMacroFocus('carbs', macroOverrides.carbs ?? manualEntryResult.carbs ?? 0)}
                    onChange={(e) => handleMacroChange('carbs', e.target.value)}
                    onBlur={() => handleMacroBlur('carbs')}
                    style={{
                      width: '50px',
                      padding: '4px',
                      border: 'none',
                      background: 'transparent',
                      color: macroOverrides.carbs !== null ? '#667eea' : '#FF9800',
                      fontSize: '18px',
                      fontWeight: '600',
                      textAlign: 'center',
                      WebkitAppearance: 'none',
                      MozAppearance: 'textfield'
                    }}
                  />
                  <span style={{ fontSize: '14px', color: macroOverrides.carbs !== null ? '#667eea' : '#FF9800' }}>g</span>
                </div>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block' }}>
                  Carbs {macroOverrides.carbs !== null && '✓'}
                </span>
              </div>
              
              {/* Fat */}
              <div 
                data-testid="manual-entry-fat"
                style={{ 
                  padding: '10px', 
                  background: 'rgba(255,255,255,0.05)', 
                  borderRadius: '8px', 
                  textAlign: 'center',
                  border: macroOverrides.fat !== null ? '2px solid #667eea' : '1px solid transparent',
                  animation: justEdited === 'fat' ? 'macroPulse 0.6s ease-out' : 'none',
                  transition: 'border-color 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                  <input
                    inputMode="numeric"
                    pattern="[0-9]*"
                    data-testid="manual-macro-input-fat"
                    value={editingField === 'fat' ? editValue : String(macroOverrides.fat ?? manualEntryResult.fat ?? 0)}
                    onFocus={() => handleMacroFocus('fat', macroOverrides.fat ?? manualEntryResult.fat ?? 0)}
                    onChange={(e) => handleMacroChange('fat', e.target.value)}
                    onBlur={() => handleMacroBlur('fat')}
                    style={{
                      width: '50px',
                      padding: '4px',
                      border: 'none',
                      background: 'transparent',
                      color: macroOverrides.fat !== null ? '#667eea' : '#E91E63',
                      fontSize: '18px',
                      fontWeight: '600',
                      textAlign: 'center',
                      WebkitAppearance: 'none',
                      MozAppearance: 'textfield'
                    }}
                  />
                  <span style={{ fontSize: '14px', color: macroOverrides.fat !== null ? '#667eea' : '#E91E63' }}>g</span>
                </div>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block' }}>
                  Fat {macroOverrides.fat !== null && '✓'}
                </span>
              </div>
            </div>
            
            {/* Reset button when overrides exist */}
            {hasOverrides && (
              <button
                onClick={() => setMacroOverrides({ calories: null, protein: null, carbs: null, fat: null })}
                style={{
                  display: 'block',
                  margin: '0 auto 12px',
                  padding: '4px 12px',
                  fontSize: '10px',
                  color: 'var(--text-muted)',
                  background: 'transparent',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
              >
                Reset to AI estimate
              </button>
            )}
            
            <NutritionDataDisclaimer />
            
            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
              {/* Don't Log Button */}
              <button 
                onClick={handleClose}
                style={{
                  padding: '14px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  background: 'rgba(239, 68, 68, 0.15)',
                  color: '#ef4444',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
              >
                ✕ Don&apos;t Log
              </button>
              
              {/* PRIMARY CTA: Add to Meals */}
              <button 
                onClick={handleAddToMeals}
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                }}
              >
                ✅ Add to My Meals
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualEntryModal;
